// 002 — Consulta Externa / Anamnesis-Examen Físico (Fase 7, Tanda 17). Hoja 1 idx 9 (A-G) y hoja 2 idx 10 (H-K), footer
// `SNS-MSP/HCU-form.002/2021`. Decisiones comunes con 003 en `bloquesAnamnesisDatos.ts`. Propio de 002: motivo con casillas
// Primera/Subsecuente, antecedentes personales con las mismas 10 categorías que los familiares, constantes vitales en 2
// tomas (con fecha, hora, perímetro abdominal, hemoglobina y glucosa capilar). Una consulta = un guardado completo (hoja 1 + 2).
// Retirados por no estar en el papel: "antecedentes alérgicos" y "gineco-obstétricos" como campos propios (el papel solo los
// nombra en el rótulo de C: se anotan en la descripción de antecedentes personales).
import type { DiagnosticoConMarca } from './MarcaDiagnostico';
import { valoresVacios, type CampoSimple, type OpcionCampo, type Valores } from './camposSimplesDatos';
import {
  CAMPOS_ENFERMEDAD_ANAMNESIS,
  CAMPOS_EXAMEN_REGIONAL,
  CAMPOS_EXAMEN_SISTEMICO,
  CAMPOS_FAMILIARES_ANAMNESIS,
  CAMPOS_PLAN_ANAMNESIS,
  CAMPOS_REVISION_ANAMNESIS,
  casillasConDescribe,
  CATEGORIAS_ANTECEDENTES,
  diagnosticosVacios,
  hallazgosExamenFisico,
  paresBloque,
  paresDiagnosticos,
} from './bloquesAnamnesisDatos';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';
import type { TomaSignos } from './signosVitalesToma';
import { imcTexto } from './calculosClinicosDatos';
import type { Par } from './index';

const opciones = (...e: readonly string[]): readonly OpcionCampo[] => e.map((x) => ({ valor: x, etiqueta: x }));

export const CAMPOS_MOTIVO_002: readonly CampoSimple[] = [
  { clave: 'tipoConsulta', etiqueta: 'Tipo de consulta', tipo: 'select', opciones: opciones('Primera', 'Subsecuente') },
  { clave: 'motivo', etiqueta: 'Motivo de consulta', tipo: 'area' },
];
export const CAMPOS_PERSONALES_002 = casillasConDescribe('ap', CATEGORIAS_ANTECEDENTES, 'Describa (antecedentes clínico-quirúrgicos, obstétricos, alérgicos relevantes)');

export const FILAS_CONSTANTES_002: readonly FilaTablaConColumnas[] = [
  { clave: 'toma1', etiqueta: 'Toma 1' },
  { clave: 'toma2', etiqueta: 'Toma 2' },
];
export const COLUMNAS_CONSTANTES_002: readonly ColumnaTablaConColumnas[] = [
  { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
  { clave: 'hora', etiqueta: 'Hora' },
  { clave: 'temperatura', etiqueta: 'Temperatura (°C)' },
  { clave: 'presion', etiqueta: 'Presión arterial (mmHg)' },
  { clave: 'pulso', etiqueta: 'Pulso/min' },
  { clave: 'fr', etiqueta: 'Frecuencia respiratoria/min' },
  { clave: 'peso', etiqueta: 'Peso (Kg)' },
  { clave: 'talla', etiqueta: 'Talla (cm)' },
  // IMC calculado desde peso y talla de la MISMA toma (solo lectura).
  { clave: 'imc', etiqueta: 'IMC (Kg/m²)', calculada: (fila) => imcTexto(fila.peso, fila.talla) },
  { clave: 'perimetro', etiqueta: 'Perímetro abdominal (cm)' },
  { clave: 'hemoglobina', etiqueta: 'Hemoglobina capilar (g/dl)' },
  { clave: 'glucosa', etiqueta: 'Glucosa capilar (mg/dl)' },
  { clave: 'spo2', etiqueta: 'Pulsioximetría (%)' },
];

export interface Estado002 {
  readonly motivo: Valores;
  readonly personales: Valores;
  readonly familiares: Valores;
  readonly enfermedad: Valores;
  readonly constantes: EstadoTablaConColumnas;
  readonly revision: Valores;
  readonly regional: Valores;
  readonly sistemico: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly plan: Valores;
}

export function estadoInicial002(): Estado002 {
  return {
    motivo: valoresVacios(CAMPOS_MOTIVO_002),
    personales: valoresVacios(CAMPOS_PERSONALES_002),
    familiares: valoresVacios(CAMPOS_FAMILIARES_ANAMNESIS),
    enfermedad: valoresVacios(CAMPOS_ENFERMEDAD_ANAMNESIS),
    constantes: estadoInicialTablaConColumnas(FILAS_CONSTANTES_002, COLUMNAS_CONSTANTES_002),
    revision: valoresVacios(CAMPOS_REVISION_ANAMNESIS),
    regional: valoresVacios(CAMPOS_EXAMEN_REGIONAL),
    sistemico: valoresVacios(CAMPOS_EXAMEN_SISTEMICO),
    diagnosticos: diagnosticosVacios(),
    plan: valoresVacios(CAMPOS_PLAN_ANAMNESIS),
  };
}

export const motivoDe002 = (e: Estado002): string => (e.motivo.motivo ?? '').trim();
export const hallazgosDe002 = (e: Estado002): string => hallazgosExamenFisico(e.regional, e.sistemico);

/** Las 2 tomas de constantes vitales como `TomaSignos` (fecha y hora propias de cada toma). */
export function tomasDe002(e: Estado002): TomaSignos[] {
  return FILAS_CONSTANTES_002.map((f) => {
    const c = e.constantes[f.clave] ?? {};
    return { fecha: c.fecha, hora: c.hora, temperatura: c.temperatura, presion: c.presion, fc: c.pulso, fr: c.fr, peso: c.peso, talla: c.talla, spo2: c.spo2 };
  });
}

export function paresDe002(e: Estado002): Par[] {
  return [
    ...paresBloque(CAMPOS_MOTIVO_002, e.motivo, ''),
    ...paresBloque(CAMPOS_PERSONALES_002, e.personales, 'Antecedentes patológicos personales — '),
    ...paresBloque(CAMPOS_FAMILIARES_ANAMNESIS, e.familiares, 'Antecedentes patológicos familiares — '),
    ...paresBloque(CAMPOS_ENFERMEDAD_ANAMNESIS, e.enfermedad, ''),
    ...FILAS_CONSTANTES_002.map((f) => ({ pregunta: `Constantes vitales y antropometría — ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(e.constantes, f.clave, COLUMNAS_CONSTANTES_002) })),
    ...paresBloque(CAMPOS_REVISION_ANAMNESIS, e.revision, 'Revisión de órganos y sistemas — '),
    ...paresBloque(CAMPOS_EXAMEN_REGIONAL, e.regional, 'Examen físico regional — '),
    ...paresBloque(CAMPOS_EXAMEN_SISTEMICO, e.sistemico, 'Examen físico sistémico — '),
    ...paresDiagnosticos(e.diagnosticos),
    ...paresBloque(CAMPOS_PLAN_ANAMNESIS, e.plan, ''),
  ].filter((p) => p.respuesta.trim() !== '');
}
